import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container } from '../../../ui/gridSystem';
import {
  PageSubtitle,
  SortingWrapper,
  Legend,
  CourseCard,
  CourseCardMobile,
} from './style';
import { Text, CTA, CustomInput, NoResult } from '../../../ui/atoms';
import { MODAL_OPEN } from '../../redux/actions';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import Pagination from '../../../ui/atoms/Pagination';
import { STATS_GROUP_LIST_GET } from '../../redux/actions/statistics';
import { timestampToDate } from '../../../utils/common';
import SortBy from '../../../ui/atoms/SortBy';
import ModalGroups, {
  ModalGroupsId,
} from '../../../ui/atoms/Modals/ModalGroups';
import MultiActions from '../../../ui/atoms/MultiActions';
import ModalGroupCourses, {
  ModalGroupCoursesId,
} from '../../../ui/atoms/Modals/ModalGroupCourses';
import ModalSelectTutor, {
  ModalSelectTutorId,
} from '../../../ui/atoms/Modals/ModalSelectTutor';
import ModalConfirm, {
  ModalConfirmId,
} from '../../../ui/atoms/Modals/ModalConfirm';
import { GROUP_DELETE, REMOVE_NEW_GROUP } from '../../redux/actions/tutors';
import ModalGroupImport, {
  ModalGroupImportId,
} from '../../../ui/atoms/Modals/ModalGroupImport';

const MyGroups = ({
  mediaIsPhone,
  isSupervisor,
  list,
  pager,
  getMyGroups,
  modalOpen,
  resetNewGroupIds,
  deleteGroups,
}) => {
  const [selectedTutor, setSelectedTutor] = useState();
  const [selectedGroup, setSelectedGroup] = useState();
  // filters
  const [selectedFilters, setSelectedFilters] = useState({
    items_per_page: 12,
  });
  const [searchValue, setSearchValue] = useState();

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => resetNewGroupIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getMyGroups(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const onClickSearch = () => {
    setSelectedFilters({ ...selectedFilters, search_text: searchValue });
  };

  const loadMore = (page) => getMyGroups({ ...selectedFilters, page });
  const setItemsPerPage = (v) =>
    getMyGroups({ ...selectedFilters, items_per_page: v?.value });

  const getActions = useCallback(
    (item) => {
      const actions = [
        {
          label: 'Visualizza i percorsi formativi',
          icon: 'eye',
          onClick: () => modalOpen(ModalGroupCoursesId, item),
        },
      ];
      if (isSupervisor || item?.owned) {
        actions.push({
          label: 'Modifica gruppo',
          icon: 'edit-05',
          onClick: () => modalOpen(ModalGroupsId, item),
        });
      }
      if (isSupervisor || item?.owned) {
        actions.push({
          label: 'Import massivo',
          icon: 'users-plus',
          onClick: () => modalOpen(ModalGroupImportId, item),
        });
      }
      if (isSupervisor) {
        actions.push({
          label: 'Elimina gruppo',
          icon: 'trash-01',
          onClick: () => {
            setSelectedGroup(item);
            modalOpen(ModalConfirmId);
          },
        });
      }
      return actions;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSupervisor]
  );

  return (
    <>
      <Container>
        <Text type="h1" mb={8}>
          I tuoi gruppi
        </Text>
        <PageSubtitle>
          <Text type="b2">
            In questa pagina è possibile visualizzare
            {isSupervisor
              ? ' tutti i gruppi presenti sulla piattaforma '
              : ' i tuoi gruppi '}
            oppure crearne di nuovi.
          </Text>
          <CTA
            type="tertiary"
            btnText="Crea nuovo gruppo"
            icon="plus-circle"
            onClick={() => modalOpen(ModalGroupsId)}
          />
        </PageSubtitle>
        <section>
          <div className="mb-4 d-flex g-16">
            <CustomInput
              size="l"
              placeholder="Cosa vuoi cercare?..."
              isSearch
              value=""
              icon="search-sm"
              isClearable
              onChange={(v) => setSearchValue(v)}
              onClickIcon={onClickSearch}
              onKeyPress={(event) => {
                if (event.key === 'Enter') onClickSearch();
              }}
            />
            {isSupervisor && (
              <>
                <Text type="b2">oppure</Text>
                <CTA
                  type="secondary"
                  btnText={selectedTutor ? 'Modifica tutor' : 'Seleziona tutor'}
                  onClick={() => modalOpen(ModalSelectTutorId)}
                />
              </>
            )}
          </div>
          <SortingWrapper>
            <div className="d-flex">
              <Text type="b3">
                {pager?.total_items ?? 0} gruppi creati
                {selectedTutor ? ' da ' : ''}
              </Text>
              {selectedTutor && <Text type="h3">{selectedTutor?.label}</Text>}
            </div>
            <SortBy
              onChange={(v) => {
                setSelectedFilters({ ...selectedFilters, sort_by: v?.value });
              }}
            />
          </SortingWrapper>
          {list?.length > 0 ? (
            <>
              {!mediaIsPhone && (
                <Legend>
                  <Text type="h3.5">Nome gruppo</Text>
                  <Text type="h3.5">Numero di utenti</Text>
                  <Text type="h3.5">Percorsi formativi collegati</Text>
                  <Text type="h3.5">Data di creazione</Text>
                </Legend>
              )}
              <div className="pb-4">
                {list.map((item, index) => (
                  <div key={`card-${index}`}>
                    {mediaIsPhone ? (
                      <CourseCardMobile>
                        <div />
                        <div>
                          <MultiActions actions={getActions(item)} />
                        </div>
                        <Text type="h5">Nome gruppo</Text>
                        <Text type="h3">{item.name}</Text>
                        <Text type="h5">Numero di utenti</Text>
                        <Text type="b3">{item?.total_users}</Text>
                        <Text type="h5">Percorsi formativi collegati</Text>
                        <Text type="b3">{item?.courses_by_group}</Text>
                        <Text type="h5">Data di creazione</Text>
                        <Text type="b4">
                          {timestampToDate(item?.creation_date)}
                        </Text>
                      </CourseCardMobile>
                    ) : (
                      <CourseCard>
                        <Text type="h3" className="ellipsis">
                          {item.name}
                        </Text>
                        <Text type="b2">{item?.total_users}</Text>
                        <Text type="b2">{item?.courses_by_group}</Text>
                        <Text type="b4">
                          {timestampToDate(item?.creation_date)}
                        </Text>
                        <div>
                          <MultiActions actions={getActions(item)} />
                        </div>
                      </CourseCard>
                    )}
                  </div>
                ))}
              </div>
              <Pagination
                pager={pager}
                callBack={loadMore}
                setItemsPerPage={setItemsPerPage}
              />
            </>
          ) : (
            <NoResult />
          )}
        </section>
      </Container>
      <ModalGroups />
      <ModalGroupImport />
      <ModalGroupCourses />
      <ModalSelectTutor
        onConfirm={(v) => {
          setSelectedFilters({ ...selectedFilters, tutor_id: v?.value });
          setSelectedTutor(v);
        }}
      />
      <ModalConfirm
        title="Elimina gruppo"
        onClick={() =>
          deleteGroups([selectedGroup?.id], () => getMyGroups(selectedFilters))
        }
      >
        <Text type="b3">
          Vuoi eliminare definitivamente il gruppo "{selectedGroup?.name}"?
        </Text>
      </ModalConfirm>
    </>
  );
};

MyGroups.propTypes = {
  mediaIsPhone: PropTypes.bool,
  // state
  isSupervisor: PropTypes.bool,
  list: PropTypes.array,
  pager: PropTypes.object,

  // dispatch
  modalOpen: PropTypes.func,
  getMyGroups: PropTypes.func,
  resetNewGroupIds: PropTypes.func,
  deleteGroups: PropTypes.func,
};

export default connect(
  (state) => {
    const { isSupervisor } = state.user;
    const { list, pager } = state.statistics.groups;
    return {
      isSupervisor,
      list,
      pager,
    };
  },
  (dispatch) => ({
    modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
    getMyGroups: (filters) =>
      dispatch({ type: STATS_GROUP_LIST_GET._REQUEST, filters }),
    resetNewGroupIds: () => dispatch({ type: REMOVE_NEW_GROUP, id: 'all' }),
    deleteGroups: (ids, callback) =>
      dispatch({ type: GROUP_DELETE._REQUEST, ids, callback }),
  })
)(withMediaQueries(MyGroups));
